
import { useState } from 'react';
import { toast } from 'sonner';
import { usePermissions } from '@/contexts/unified-permissions';
import { FeedbackFormData } from '@/types/feedback';
import { uploadFeedbackScreenshot, submitFeedbackTicket, sendConfirmationEmail } from '@/services/feedbackService';

export const useFeedbackForm = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { user } = usePermissions();

  const handleSubmitFeedback = async (data: FeedbackFormData) => {
    if (!user) {
      toast.error('You must be logged in to submit feedback');
      return;
    }

    setIsSubmitting(true);
    try {
      let screenshotUrl: string | undefined = undefined;
      
      // If a screenshot was provided, upload it to storage
      if (data.screenshot) {
        screenshotUrl = await uploadFeedbackScreenshot(data.screenshot, user.id);
        if (!screenshotUrl) {
          toast.error('Failed to upload screenshot. Feedback will be submitted without it.');
        }
      }
      
      // Submit the feedback
      const submittedData = await submitFeedbackTicket(data, user.id, screenshotUrl);
      
      // Send confirmation email (optional - don't fail if email service is down)
      if (submittedData && submittedData.data && submittedData.data[0]) {
        const ticketData = submittedData.data[0];
        try {
          await sendConfirmationEmail(ticketData, user.id, data);
        } catch (emailError) {
          console.warn('Failed to send confirmation email, but feedback was submitted successfully:', emailError);
          // Don't throw error - feedback submission was successful
        }
      }
      
      // Show success toast
      if (data.type === 'Technique Request') {
        toast.success('Technique request submitted successfully!');
      } else {
        toast.success('Feedback submitted successfully!');
      }
      
      return true;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    submitFeedback: handleSubmitFeedback
  };
};
